import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useChildProfile } from "../contexts/ChildProfileContext";
import { BigButton } from "../design/BigButton";
import { ASSETS, AVATAR_EMOJI } from "../lib/assets";
import { speech } from "../audio/speechService";
import "./onboarding.css";

/**
 * First-time parent onboarding — 7 steps. Skipped entirely if the account
 * already has a child profile. Landing URL: /register/setup.
 */

const AGE_BANDS = [
  { key: "2-4", label: "Ages 2 to 4", difficulty: "easy" },
  { key: "3-4", label: "Ages 3 to 4", difficulty: "easy" },
  { key: "4-6", label: "Ages 4 to 6", difficulty: "easy" },
  { key: "6-8", label: "Ages 6 to 8", difficulty: "medium" },
];

const NARRATION_SPEEDS = [
  { key: "slow", label: "Slow", sample: "Rub-ber duck." },
  { key: "normal", label: "Normal", sample: "Rubber duck." },
  { key: "fast", label: "Fast", sample: "Rubber duck." },
];

const AVATAR_LIST = ["fox", "bear", "bunny", "cat", "frog", "panda"];

export function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { refresh: refreshProfiles } = useChildProfile();
  const [step, setStep] = useState(0);
  const [state, setState] = useState({
    ageBand: "4-6",
    difficulty: "easy",
    narrationSpeed: "slow",
    language: "en",
    avatar: "fox",
    nickname: "",
    pin: "",
    pinConfirm: "",
    acceptTerms: false,
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Skip onboarding if already has a child profile.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profiles");
        if ((data.items || []).length > 0) navigate("/profiles", { replace: true });
      } catch (_e) { /* not authed → RequireAuth handles it */ }
    })();
  }, [navigate]);

  function updateAgeBand(k) {
    const band = AGE_BANDS.find((b) => b.key === k);
    setState((s) => ({ ...s, ageBand: k, difficulty: band?.difficulty || s.difficulty }));
  }

  const canProceed = {
    0: true,
    1: !!state.ageBand,
    2: !!state.difficulty,
    3: !!state.narrationSpeed,
    4: !!state.language,
    5: !!state.avatar,
    6: state.pin.length >= 4 && state.pin === state.pinConfirm,
    7: state.acceptTerms,
  }[step];

  async function finish() {
    setBusy(true); setErr(null);
    try {
      // Set PIN
      await api.post("/auth/set-pin", { pin: state.pin });
      // Create first child profile
      await api.post("/profiles", {
        nickname: (state.nickname || "Explorer").trim(),
        avatar: state.avatar,
        ageBand: state.ageBand,
        difficulty: state.difficulty,
        narrationSpeed: state.narrationSpeed,
        language: state.language,
      });
      await refresh();
      await refreshProfiles();
      navigate("/profiles", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <div className="wmleye-app onboarding" data-testid="onboarding-wizard">
      <header className="onboarding__header">
        <img src={ASSETS.mascot} alt="Ollie the owl" className="onboarding__mascot" />
        <div>
          <h1 data-testid="onboarding-step-title">{TITLES[step]}</h1>
          <p className="onboarding__progress" data-testid="onboarding-step-progress">
            Step {step + 1} of {TITLES.length}
          </p>
        </div>
      </header>

      {err && <div className="onboarding__error" data-testid="onboarding-error">{err}</div>}

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          className="onboarding__step"
        >
          {step === 0 && (
            <div data-testid="onb-welcome">
              <p style={{ fontSize: "1.1rem" }}>
                Welcome to <b>With My Little EYE</b>! In the next few minutes we&apos;ll
                set up your first child profile — no personal info about your
                child is required (no full name, no birthday, no photos).
              </p>
              <p style={{ color: "var(--wmleye-ink-soft)" }}>
                You&apos;ll pick an age range, a difficulty, and a friendly avatar.
                Everything is adjustable later.
              </p>
            </div>
          )}
          {step === 1 && (
            <div className="onboarding__grid" data-testid="onb-ageband">
              {AGE_BANDS.map((b) => (
                <button
                  key={b.key}
                  className={`onboarding__choice ${state.ageBand === b.key ? "selected" : ""}`}
                  onClick={() => updateAgeBand(b.key)}
                  data-testid={`onb-age-${b.key}`}
                >
                  <b>{b.label}</b>
                  <span>Recommended: {b.difficulty}</span>
                </button>
              ))}
            </div>
          )}
          {step === 2 && (
            <div className="onboarding__grid" data-testid="onb-difficulty">
              {["easy", "medium", "hard"].map((d) => (
                <button
                  key={d}
                  className={`onboarding__choice ${state.difficulty === d ? "selected" : ""}`}
                  onClick={() => setState({ ...state, difficulty: d })}
                  data-testid={`onb-difficulty-${d}`}
                >
                  <b style={{ textTransform: "capitalize" }}>{d}</b>
                  <span>
                    {d === "easy" && "Bigger hitboxes, always free hints"}
                    {d === "medium" && "Standard hitboxes, 2 free hints per level"}
                    {d === "hard" && "Tight hitboxes, 1 free hint per level"}
                  </span>
                </button>
              ))}
            </div>
          )}
          {step === 3 && (
            <div className="onboarding__grid" data-testid="onb-speed">
              {NARRATION_SPEEDS.map((s) => (
                <div key={s.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <button
                    className={`onboarding__choice ${state.narrationSpeed === s.key ? "selected" : ""}`}
                    onClick={() => setState({ ...state, narrationSpeed: s.key })}
                    data-testid={`onb-speed-${s.key}`}
                  >
                    <b>{s.label}</b><span>Narration pace</span>
                  </button>
                  <BigButton variant="ghost" onClick={() => speech.speak(s.sample, { speed: s.key })} data-testid={`onb-speed-try-${s.key}`}>
                    Try it
                  </BigButton>
                </div>
              ))}
            </div>
          )}
          {step === 4 && (
            <div data-testid="onb-language">
              <label>
                Choose language
                <select value={state.language} onChange={(e) => setState({ ...state, language: e.target.value })} data-testid="onb-language-select">
                  <option value="en">English (US)</option>
                  <option value="en-GB" disabled>English (UK) — coming soon</option>
                  <option value="es" disabled>Español — coming soon</option>
                  <option value="fr" disabled>Français — coming soon</option>
                </select>
              </label>
            </div>
          )}
          {step === 5 && (
            <div data-testid="onb-avatar">
              <label>
                Nickname (optional)
                <input value={state.nickname} onChange={(e) => setState({ ...state, nickname: e.target.value })} maxLength={16} data-testid="onb-nickname" />
              </label>
              <div className="onboarding__avatars">
                {AVATAR_LIST.map((a) => (
                  <button
                    key={a}
                    className={`onboarding__avatar ${state.avatar === a ? "selected" : ""}`}
                    onClick={() => setState({ ...state, avatar: a })}
                    data-testid={`onb-avatar-${a}`}
                  >
                    <span style={{ fontSize: 28 }}>{AVATAR_EMOJI[a]}</span>
                    <span>{a}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 6 && (
            <div data-testid="onb-pin">
              <p style={{ color: "var(--wmleye-ink-soft)" }}>
                Your PIN guards settings, purchases and account controls. 4 to 6
                digits. Never share it with your child.
              </p>
              <div className="editor-field-row" style={{ gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                <label>PIN
                  <input
                    type="password" inputMode="numeric" maxLength={6}
                    value={state.pin}
                    onChange={(e) => setState({ ...state, pin: e.target.value.replace(/\D/g, "") })}
                    data-testid="onb-pin-input"
                  />
                </label>
                <label>Confirm
                  <input
                    type="password" inputMode="numeric" maxLength={6}
                    value={state.pinConfirm}
                    onChange={(e) => setState({ ...state, pinConfirm: e.target.value.replace(/\D/g, "") })}
                    data-testid="onb-pin-confirm"
                  />
                </label>
              </div>
              {state.pin && state.pinConfirm && state.pin !== state.pinConfirm && (
                <p style={{ color: "#8a4a10" }}>PINs don&apos;t match yet.</p>
              )}
            </div>
          )}
          {step === 7 && (
            <div data-testid="onb-privacy">
              <h3>Privacy summary</h3>
              <ul style={{ lineHeight: 1.7, paddingLeft: "1rem" }}>
                <li>No third-party ads, ever.</li>
                <li>No child tracking or profiling.</li>
                <li>All settings are behind a parent PIN.</li>
                <li>You can export or delete everything anytime.</li>
              </ul>
              <label style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", marginTop: "0.75rem" }}>
                <input
                  type="checkbox"
                  checked={state.acceptTerms}
                  onChange={(e) => setState({ ...state, acceptTerms: e.target.checked })}
                  data-testid="onb-accept-terms"
                />
                I agree to the Privacy Policy and Terms.
              </label>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="onboarding__nav">
        <BigButton variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onb-back">
          ← Back
        </BigButton>
        {step < TITLES.length - 1 ? (
          <BigButton disabled={!canProceed} onClick={() => setStep(step + 1)} data-testid="onb-next">
            Next
          </BigButton>
        ) : (
          <BigButton disabled={!canProceed || busy} onClick={finish} data-testid="onb-finish">
            {busy ? "Creating…" : "Create profile"}
          </BigButton>
        )}
      </div>
    </div>
  );
}

const TITLES = [
  "Welcome!",
  "Pick an age range",
  "Choose difficulty",
  "Narration speed",
  "Language",
  "Child avatar",
  "Create a parent PIN",
  "Privacy",
];
